/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React from 'react'
import { Box, Menu, MenuItem, Paper, Table, TableBody, TableContainer, TableHead, TableRow, styled } from '@mui/material'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableSortLabel from '@mui/material/TableSortLabel'
import { visuallyHidden } from '@mui/utils'
import Pagination from '../../../../components/TablePagination'
import { parseDateInAOHstandard } from '../../../../customHooks/parseDate'
import LanguageStrings from '../../../../i18n/locales'
import IconButton from '@mui/material/IconButton'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { type testConnectionState } from '../../redux/testConnectionSlice'
import { type RootState } from '../../../../store'
import { type ConnectedProps, connect } from 'react-redux'
import { TestConnectionWithoutCredentialsService } from '../../redux/actionCreators'
import Notification from '../../../../components/Notification'

const AssetConfigurationStrings = LanguageStrings().ConnectionSettings.ConnectionTable

// Define the HeadCell interface
interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

// Styled TableCell for consistent design
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'connectionName',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.ConnectionName,
    sortable: true
  },
  {
    id: 'protocolTypeName',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.ProtocolTypeName,
    sortable: true
  },
  {
    id: 'protocolName',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.ProtocolName,
    sortable: true
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.Description,
    sortable: true
  },
  {
    id: 'modifiedAt',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.ModifiedAt,
    sortable: true
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: AssetConfigurationStrings.ACTION,
    sortable: false
  }
]

interface Data {
  modifiedAt: string | null
  connectionName: string
  description: string | null
  protocolTypeName: string
  protocolName: string
  protocolId: string
}
interface ConfigurationDetailsProps {
  style: React.CSSProperties
  configurationList: Data[]
  handleEditClick: (protocolTypeName: string, protocolId: string) => void
  count: number
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  rowsPerPage: number
  setSort: React.Dispatch<React.SetStateAction<Sort>>
  sort: Sort
}

export type Order = 'asc' | 'desc'
export interface Sort {
  order: Order
  orderBy: keyof Data
}

interface EnhancedTableHeadProps {
  order: 'asc' | 'desc'
  orderBy: keyof ConfigurationDetailsProps['configurationList'][0]
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof ConfigurationDetailsProps['configurationList'][0]
  ) => void
}

function EnhancedTableHead (props: EnhancedTableHeadProps) {
  const { order, orderBy, onRequestSort } = props

  const createSortHandler =
    (newOrderBy: keyof ConfigurationDetailsProps['configurationList'][0]) =>
      (event: React.MouseEvent<unknown>) => {
        onRequestSort(event, newOrderBy)
      }

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <StyledTableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable
              ? (
              <TableSortLabel
                className='custom-tablesort-label'
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                sx={{ cursor: headCell.sortable ? 'pointer' : 'text', color: '#F0F0F0' }}
              >
                {headCell.label}
                {orderBy === headCell.id
                  ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                  </Box>
                    )
                  : null}
              </TableSortLabel>
                )
              : (
                  headCell.label
                )}
          </StyledTableCell>
        ))}
      </TableRow>
    </TableHead>
  )
}

function truncateValue (text: string | null): JSX.Element {
  return ((text != null) && text.length > 12
    ? <abbr style={{ textDecoration: 'none' }} title={text}>{text.slice(0, 12)}...</abbr>
    : <span>{text}</span>)
}
type allConfigurationDetailsProps = ConfigurationDetailsProps & ReduxProps

const ConfigurationDetails = (props: allConfigurationDetailsProps): JSX.Element => {
  const [openMenuFor, setOpenMenuFor] = React.useState<string | null>(null)
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const [showNotification, setShowNotification] = React.useState<boolean>(false)

  const openIdForRow = (row: Data, index: number) =>
    row.protocolId ?? String(index)

  const handleOpenMenu = (e: React.MouseEvent, row: Data, index: number) => {
    e.stopPropagation()
    const id = openIdForRow(row, index)
    setAnchorEl(e.currentTarget as HTMLElement)
    setOpenMenuFor(id)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
    setOpenMenuFor(null)
  }

  const handleTestConnection = (protocolId: string): void => {
    props.testConnectionWithoutCredentials(protocolId)
    setShowNotification(true)
  }
  const handleCloseNotification = (): void => {
    setShowNotification(false)
  }
  const handleRequestSort =
    (event: React.MouseEvent<unknown>, newOrderBy: keyof Data) => {
      const isAsc = props.sort.orderBy === newOrderBy && props.sort.order === 'asc'
      const toggledOrder = isAsc ? 'desc' : 'asc'
      props.setSort({ order: toggledOrder, orderBy: newOrderBy })
    }

  return (
    <Box sx={{ ...props.style, overflowX: 'auto', padding: '0px' }}>
      <Paper sx={{ width: '100%', mb: 2, backgroundColor: '#272727', boxShadow: 'unset', color: '#F0F0F0' }}>
        <TableContainer>
          <Table sx={{ minWidth: 750, color: '#f0f0f0' }} aria-labelledby="tableTitle" size={'small'}>
            <EnhancedTableHead order={props.sort.order} orderBy={props.sort.orderBy} onRequestSort={handleRequestSort} />
            <TableBody>
              {props.configurationList.map((row, index) => (
                <TableRow hover key={index}>
                  <StyledTableCell>{truncateValue(row.connectionName)}</StyledTableCell>
                  <StyledTableCell>{row.protocolTypeName}</StyledTableCell>
                  <StyledTableCell>{truncateValue(row.protocolName)}</StyledTableCell>
                  <StyledTableCell>{truncateValue(row.description)}</StyledTableCell>
                  <StyledTableCell>{row.modifiedAt !== null ? parseDateInAOHstandard(row.modifiedAt) : ''}</StyledTableCell>
                  <StyledTableCell
                    sx={{
                      color: '#64C3FF !important',
                      cursor: 'pointer'
                    }}
                  >
                    <>
                      <div
                        style={{ display: 'inline', paddingRight: '10px' }}
                        onClick={() => {
                          props.handleEditClick(row.protocolTypeName, row.protocolId)
                          handleCloseMenu()
                        }}
                      >
                        {AssetConfigurationStrings.Edit}
                      </div>
                      <div
                        style={{
                          display: 'inline-flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          position: 'relative',
                          paddingBottom: '3px'
                        }}
                      >
                      <IconButton
                        aria-controls={`menu-${index}`}
                        aria-haspopup="true"
                        data-testid="device-ellipsis"
                        sx={{ padding: '0px !important', position: 'absolute' }}
                        onClick={(event) => { handleOpenMenu(event, row, index) }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                          <Menu
                            id={`menu-${index}`}
                            anchorEl={anchorEl}
                            open={openMenuFor === openIdForRow(row, index)}
                            onClose={handleCloseMenu}
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right'
                            }}
                            transformOrigin={{
                              vertical: 'top',
                              horizontal: 'right'
                            }}
                            PaperProps={{
                              className: 'customMenuPaper'
                            }}
                            MenuListProps={{
                              className: 'myMenuList'
                            }}
                          >
                            <MenuItem
                              data-testid="testconnection-btn"
                              style={{
                                padding: '10px 30px 10px 12px',
                                fontSize: '14px',
                                fontFamily: 'Honeywell Sans Web'
                              }}
                              onClick={() => {
                                handleTestConnection(row.protocolId)
                                handleCloseMenu()
                              }}
                            >
                              {AssetConfigurationStrings.TestConnection}
                            </MenuItem>
                          </Menu>
                      </div>
                    </>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {props.count > 10 && (<Pagination count={props.count} page={props.page} setPage={props.setPage} rowsPerPage={props.rowsPerPage} siblingCount={1}
                            boundaryCount={1}></Pagination>)}
      </Paper>
      {showNotification && (
        <Notification data-testid='notification' containerStyle={{ position: 'absolute', bottom: '70px', right: '30px' }} httpStatus={props.TestConnectionData.httpStatus ?? 0} message={props.TestConnectionData.message ?? ''} loading={props.TestConnectionData.isLoading} onClose={handleCloseNotification} />
      )}
    </Box>
  )
}
interface DispatchToProps {
  testConnectionWithoutCredentials: (protocolId: string) => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  testConnectionWithoutCredentials: (protocolId: string) => dispatch(TestConnectionWithoutCredentialsService(protocolId))
})

interface StateToProps {
  TestConnectionData: testConnectionState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  TestConnectionData: state.testConnectionData
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type ReduxProps = ConnectedProps<typeof connector>

export default connector(ConfigurationDetails)
